/**
 * Group Session Store
 *
 * Zustand store for the Group Prioritization feature.
 *
 * Responsibilities:
 *  - Session lifecycle (SETUP → OPEN → SCORING → REVEALING → DECIDED)
 *  - Criteria and option management (via RPCs)
 *  - Per-member score submission with optimistic updates and an offline queue
 *  - Supabase Realtime — postgres_changes for session/events + presence channel
 *  - Automatic flush of the offline queue on reconnect
 */

import { create } from 'zustand';
import type { StateCreator } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import {
  computeGroupResults,
  type GroupSession,
  type GroupSessionStatus,
  type GroupCriterion,
  type GroupOption,
  type GroupScore,
  type GroupMemberPresence,
  type GroupSessionResults,
  type PendingScore,
  type PreS6RankingSnapshot,
} from '../../types/prioritization/groupSession';

// ─── State shape ──────────────────────────────────────────────────────────────

interface GroupSessionStoreState {
  // ── Session data ────────────────────────────────────────────────────────────
  currentSession: GroupSession | null;
  criteria: GroupCriterion[];
  options: GroupOption[];

  /**
   * The current user's submitted scores.
   * Keyed: itemId → criteriaId → GroupScore
   */
  myScores: Record<string, Record<string, GroupScore>>;

  /**
   * All member scores — populated only when status is REVEALING or DECIDED.
   * Used for result computation and display.
   */
  allScores: GroupScore[];

  /** Computed results — populated in REVEALING / DECIDED. */
  results: GroupSessionResults | null;

  // ── Presence ────────────────────────────────────────────────────────────────
  presenceMembers: GroupMemberPresence[];

  // ── Offline queue ────────────────────────────────────────────────────────────
  pendingScores: PendingScore[];
  isOnline: boolean;

  // ── UI flags ────────────────────────────────────────────────────────────────
  isLoading: boolean;
  error: string | null;
  /**
   * Per-action errors for the host controls. Kept separate from `error`
   * (which GroupSessionPage renders as a whole-page load failure) so a refused
   * reveal / winner pick shows next to the button that failed.
   */
  revealError: string | null;
  selectWinnerError: string | null;
  /** True during the reveal animation sequence. */
  revealAnimating: boolean;

  // ── Private channel refs (not exposed to consumers) ─────────────────────────
  _presenceChannel: RealtimeChannel | null;
  _sessionChannel: RealtimeChannel | null;

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Create a brand-new group session. Returns { sessionId, joinToken } or { error }. */
  createGroupSession(
    workspaceId: string,
    title: string,
    description?: string,
  ): Promise<{ sessionId: string; joinToken: string } | { error: string }>;

  /** Load all session data into the store for a given session ID. */
  loadGroupSession(sessionId: string): Promise<void>;

  /**
   * Look up a session by its join token (used when a non-facilitator follows a link).
   * Returns the sessionId on success, null if not found.
   */
  loadGroupSessionByToken(joinToken: string): Promise<string | null>;

  /**
   * Replace the full criteria list for a session.
   * Calls the set_group_criteria RPC, then re-fetches criteria from the DB.
   */
  setGroupCriteria(
    sessionId: string,
    criteria: Array<{
      title: string;
      description?: string;
      weight: number;
      position: number;
    }>,
  ): Promise<void>;

  /**
   * Add an option (workspace item) to the session.
   * Calls the add_group_option RPC, then re-fetches options.
   */
  addGroupOption(
    workspaceId: string,
    sessionId: string,
    title: string,
    description?: string,
    url?: string,
  ): Promise<void>;

  /**
   * Submit a score for one (item, criterion) pair.
   * - Online: calls the submit_group_score RPC.
   * - Offline: enqueues to pendingScores.
   * In both cases, myScores is updated optimistically and presence is broadcast.
   */
  submitScore(
    itemId: string,
    criteriaId: string,
    score: number,
    note?: string,
  ): Promise<void>;

  /**
   * Flush all pending (offline-queued) scores to the server.
   * Called automatically when the realtime channel reconnects.
   */
  flushPendingScores(): Promise<void>;

  /** Transition the session to OPEN status. */
  openGroupSession(sessionId: string): Promise<void>;

  /** Transition the session to SCORING status. */
  startScoring(sessionId: string): Promise<void>;

  /** Transition the session to REVEALING status. */
  revealResults(sessionId: string): Promise<{ ok: boolean; error?: string }>;

  /** Transition the session to DECIDED status and record the chosen winner. */
  selectWinner(sessionId: string, itemId: string): Promise<{ ok: boolean; error?: string }>;

  /**
   * Write the Pre-S6 system recommendation snapshot onto the session row.
   * Idempotent — no-op if a snapshot is already recorded.
   * Call this when opening an S6 session, passing the ranking produced by S1-S5.
   */
  capturePreS6Snapshot(sessionId: string, snapshot: PreS6RankingSnapshot): Promise<void>;

  /**
   * Fetch fully aggregated results via the get_group_results RPC and store them.
   * Can be called independently of the reveal flow (e.g. on re-mount in DECIDED).
   */
  loadResults(sessionId: string): Promise<void>;

  /**
   * Subscribe to Supabase Realtime for a session.
   * Sets up:
   *  1. postgres_changes on prioritization_sessions (status updates)
   *  2. postgres_changes on session_events (member events, reveal, winner)
   *  3. Presence channel for live member tracking
   */
  subscribeToGroupSession(sessionId: string, workspaceId: string): Promise<void>;

  /** Tear down all realtime channels for the session. */
  unsubscribeFromGroupSession(sessionId: string): void;

  /**
   * Broadcast the current user's presence state to other session members.
   * userId is inferred from the auth session — callers supply the rest.
   */
  broadcastPresence(
    sessionId: string,
    presence: Omit<GroupMemberPresence, 'userId'>,
  ): void;

  /** Reset all group session state (call when navigating away). */
  resetGroupSession(): void;

  /** Host edits session name + description (SETUP or OPEN only). */
  updateGroupSessionDetails(
    sessionId: string,
    title: string,
    description?: string,
  ): Promise<{ ok: true } | { ok: false; error: string }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Strip null bytes (\u0000) from a string before sending to PostgreSQL.
 * Postgres TEXT columns reject null characters with "null character not permitted".
 */
function sanitizeText(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/\u0000/g, '');
}

function sanitizeTextOrNull(value: string | null | undefined): string | null {
  if (value == null) return null;
  return sanitizeText(value);
}

/** Safely map a raw DB row to a GroupCriterion. */
function rowToCriterion(row: Record<string, unknown>): GroupCriterion {
  return {
    id: row['id'] as string,
    workspaceId: row['workspace_id'] as string,
    sessionId: row['session_id'] as string,
    title: row['title'] as string,
    description: (row['description'] as string | undefined) ?? undefined,
    weight: (row['weight'] as number) ?? 1,
    position: (row['position'] as number) ?? 0,
    createdBy: row['created_by'] as string,
    createdAt: new Date(row['created_at'] as string),
    updatedAt: new Date(row['updated_at'] as string),
  };
}

/** Safely map a raw DB row to a GroupScore. */
function rowToScore(row: Record<string, unknown>): GroupScore {
  return {
    id: row['id'] as string,
    workspaceId: row['workspace_id'] as string,
    sessionId: row['session_id'] as string,
    itemId: row['item_id'] as string,
    criteriaId: row['criteria_id'] as string,
    userId: row['user_id'] as string,
    score: row['score'] as number,
    note: (row['note'] as string | undefined) ?? undefined,
    createdAt: new Date(row['created_at'] as string),
    updatedAt: new Date(row['updated_at'] as string),
  };
}

function rowToOption(row: Record<string, unknown>): GroupOption {
  let url: string | undefined;
  const meta = row['metadata'];
  if (meta && typeof meta === 'object' && meta !== null && !Array.isArray(meta)) {
    const raw = (meta as Record<string, unknown>)['group_option_url'];
    if (typeof raw === 'string' && raw.trim()) url = raw.trim();
  }

  return {
    id: row['id'] as string,
    workspaceId: row['workspace_id'] as string,
    title: row['title'] as string,
    description: (row['description'] as string | undefined) ?? undefined,
    url,
  };
}

/** Map a raw prioritization_sessions row to a GroupSession. */
function rowToGroupSession(row: Record<string, unknown>): GroupSession {
  return {
    id: row['id'] as string,
    workspaceId: row['workspace_id'] as string,
    title: (row['name'] ?? row['title']) as string,
    description: (row['description'] as string | undefined) ?? undefined,
    groupSessionStatus: (row['group_session_status'] as GroupSessionStatus) ?? 'SETUP',
    joinToken: (row['group_join_token'] as string | undefined) ?? undefined,
    divergenceThreshold:
      (row['group_divergence_threshold'] as number | undefined) ??
      (row['divergence_threshold'] as number | undefined) ??
      1,
    facilitatorId: row['facilitator_id'] as string,
    itemIds: (row['item_ids'] as string[]) ?? [],
    createdAt: new Date(row['created_at'] as string),
    updatedAt: new Date(row['updated_at'] as string),
    preS6Snapshot: (row['pre_s6_ranking_snapshot'] as PreS6RankingSnapshot | undefined) ?? undefined,
  };
}

// ─── Store ────────────────────────────────────────────────────────────────────

type GroupSessionCreator = StateCreator<GroupSessionStoreState>;

const storeCreator: GroupSessionCreator = (set, get) => ({
  // ── Initial state ────────────────────────────────────────────────────────────
  currentSession: null,
  criteria: [],
  options: [],
  myScores: {},
  allScores: [],
  results: null,
  presenceMembers: [],
  pendingScores: [],
  isOnline: true,
  isLoading: false,
  error: null,
  revealError: null,
  selectWinnerError: null,
  revealAnimating: false,
  _presenceChannel: null,
  _sessionChannel: null,

  // ── createGroupSession ────────────────────────────────────────────────────────
  createGroupSession: async (workspaceId, title, description) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      return { error: 'Database not available' };
    }
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase.rpc('create_group_session', {
        p_workspace_id: workspaceId,
        p_title: sanitizeText(title),
        p_description: sanitizeTextOrNull(description),
      });
      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message };
      }
      // create_group_session (455) returns a jsonb payload rather than raising
      // on failure (e.g. "Not authenticated.", "Permission denied: must be an
      // active workspace member.") — `error` above is null in that case, so
      // the result must be checked explicitly or a failure silently reads as
      // success with an undefined sessionId/joinToken.
      const result = data as { success?: boolean; session_id?: string; join_token?: string; error?: string };
      if (!result?.success || !result.session_id || !result.join_token) {
        const message = result?.error ?? 'Could not create the group decision.';
        set({ isLoading: false, error: message });
        return { error: message };
      }
      set({ isLoading: false });
      return { sessionId: result.session_id, joinToken: result.join_token };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  // ── updateGroupSessionDetails ─────────────────────────────────────────────────
  updateGroupSessionDetails: async (sessionId, title, description) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      return { ok: false, error: 'Database not available' };
    }
    try {
      set({ error: null });
      const { data, error } = await supabase.rpc('update_group_session_details', {
        p_session_id: sessionId,
        p_name: sanitizeText(title.trim()),
        p_description: sanitizeText(description ?? ''),
      });
      if (error) {
        set({ error: error.message });
        return { ok: false, error: error.message };
      }
      const res = data as { success?: boolean; error?: string; name?: string; description?: string | null };
      if (!res?.success) {
        const msg = res?.error ?? 'Could not save decision framing';
        set({ error: msg });
        return { ok: false, error: msg };
      }

      const name = typeof res.name === 'string' ? res.name : title.trim();
      const descRaw = res.description;

      set((state) => {
        const cur = state.currentSession;
        if (!cur || cur.id !== sessionId) return state;
        return {
          currentSession: {
            ...cur,
            title: name,
            description: typeof descRaw === 'string' && descRaw.trim() ? descRaw : undefined,
          },
        };
      });
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      set({ error: message });
      return { ok: false, error: message };
    }
  },

  // ── loadGroupSession ──────────────────────────────────────────────────────────
  loadGroupSession: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return;
    }
    try {
      set({ isLoading: true, error: null });

      // Fetch the session row
      const { data: sessionRow, error: sessionError } = await supabase
        .from('prioritization_sessions')
        .select('*')
        .eq('id', sessionId)
        .not('group_session_status', 'is', null)
        .single();

      if (sessionError || !sessionRow) {
        const message = sessionError?.message ?? 'Session not found';
        set({ isLoading: false, error: message });
        return;
      }

      const session = rowToGroupSession(sessionRow as Record<string, unknown>);

      // Fetch criteria
      const { data: criteriaRows, error: criteriaError } = await supabase
        .from('group_criteria')
        .select('*')
        .eq('session_id', sessionId)
        .order('position', { ascending: true });

      if (criteriaError) {
        console.warn('[GroupSessionStore] Failed to fetch criteria:', criteriaError.message);
      }
      const criteria = (criteriaRows ?? []).map((r) =>
        rowToCriterion(r as Record<string, unknown>),
      );

      // Fetch options (items) for this session
      let options: GroupOption[] = [];
      if (session.itemIds.length > 0) {
        const { data: itemRows, error: itemsError } = await supabase
          .from('items')
          .select('id, workspace_id, title, description, metadata')
          .in('id', session.itemIds);

        if (itemsError) {
          console.warn('[GroupSessionStore] Failed to fetch items:', itemsError.message);
        }
        options = (itemRows ?? []).map((r) => rowToOption(r as Record<string, unknown>));
      }

      // Fetch the current user's scores
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const userId = user?.id;

      const myScores: Record<string, Record<string, GroupScore>> = {};
      if (userId) {
        const { data: myScoreRows, error: myScoreError } = await supabase
          .from('group_scores')
          .select('*')
          .eq('session_id', sessionId)
          .eq('user_id', userId);

        if (myScoreError) {
          console.warn('[GroupSessionStore] Failed to fetch own scores:', myScoreError.message);
        }
        for (const row of myScoreRows ?? []) {
          const score = rowToScore(row as Record<string, unknown>);
          if (!myScores[score.itemId]) myScores[score.itemId] = {};
          myScores[score.itemId][score.criteriaId] = score;
        }
      }

      // Fetch all scores if in revealing/decided state
      let allScores: GroupScore[] = [];
      let results: GroupSessionResults | null = null;
      if (
        session.groupSessionStatus === 'REVEALING' ||
        session.groupSessionStatus === 'DECIDED'
      ) {
        const { data: allScoreRows, error: allScoreError } = await supabase
          .from('group_scores')
          .select('*')
          .eq('session_id', sessionId);

        if (allScoreError) {
          console.warn('[GroupSessionStore] Failed to fetch all scores:', allScoreError.message);
        }
        allScores = (allScoreRows ?? []).map((r) => rowToScore(r as Record<string, unknown>));

        // Compute results optimistically from the fetched data
        const memberPresences = get().presenceMembers;
        results = computeGroupResults(
          criteria,
          options,
          allScores,
          session.divergenceThreshold,
          memberPresences.map((m) => ({ userId: m.userId, displayName: m.displayName })),
        );
      }

      set({
        currentSession: session,
        criteria,
        options,
        myScores,
        allScores,
        results,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] loadGroupSession error:', err);
      set({ isLoading: false, error: message });
    }
  },

  // ── loadGroupSessionByToken ───────────────────────────────────────────────────
  loadGroupSessionByToken: async (joinToken) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return null;
    }
    try {
      const { data, error } = await supabase
        .from('prioritization_sessions')
        .select('id')
        .eq('group_join_token', joinToken)
        .single();

      if (error || !data) {
        return null;
      }
      return (data as { id: string }).id;
    } catch (err) {
      console.error('[GroupSessionStore] loadGroupSessionByToken error:', err);
      return null;
    }
  },

  // ── setGroupCriteria ──────────────────────────────────────────────────────────
  setGroupCriteria: async (sessionId, criteriaInput) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return;
    }
    try {
      set({ error: null });
      const { error } = await supabase.rpc('set_group_criteria', {
        p_session_id: sessionId,
        p_criteria: criteriaInput.map((c) => ({
          ...c,
          title: sanitizeText(c.title),
          description: c.description ? sanitizeText(c.description) : c.description,
        })),
      });
      if (error) {
        console.error('[GroupSessionStore] set_group_criteria RPC error:', error.message);
        set({ error: error.message });
        return;
      }

      // Re-fetch criteria to reflect server state
      const { data: criteriaRows, error: fetchError } = await supabase
        .from('group_criteria')
        .select('*')
        .eq('session_id', sessionId)
        .order('position', { ascending: true });

      if (fetchError) {
        console.warn('[GroupSessionStore] Failed to re-fetch criteria:', fetchError.message);
        return;
      }
      const criteria = (criteriaRows ?? []).map((r) =>
        rowToCriterion(r as Record<string, unknown>),
      );
      set({ criteria });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] setGroupCriteria error:', err);
      set({ error: message });
    }
  },

  // ── addGroupOption ────────────────────────────────────────────────────────────
  addGroupOption: async (workspaceId, sessionId, title, description, url) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return;
    }
    try {
      set({ error: null });
      const { error } = await supabase.rpc('add_group_option', {
        p_workspace_id: workspaceId,
        p_session_id: sessionId,
        p_title: sanitizeText(title),
        p_description: sanitizeTextOrNull(description),
        p_url: sanitizeTextOrNull(url),
      });
      if (error) {
        console.error('[GroupSessionStore] add_group_option RPC error:', error.message);
        set({ error: error.message });
        return;
      }

      // Re-fetch session to get the updated item_ids list
      const { data: sessionRow } = await supabase
        .from('prioritization_sessions')
        .select('item_ids')
        .eq('id', sessionId)
        .single();

      const updatedItemIds = (sessionRow as { item_ids: string[] } | null)?.item_ids ?? [];

      // Re-fetch items for those IDs
      let options: GroupOption[] = [];
      if (updatedItemIds.length > 0) {
        const { data: itemRows } = await supabase
          .from('items')
          .select('id, workspace_id, title, description, metadata')
          .in('id', updatedItemIds);
        options = (itemRows ?? []).map((r) => rowToOption(r as Record<string, unknown>));
      }

      set((state) => ({
        options,
        currentSession: state.currentSession
          ? { ...state.currentSession, itemIds: updatedItemIds }
          : state.currentSession,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] addGroupOption error:', err);
      set({ error: message });
    }
  },

  // ── submitScore ───────────────────────────────────────────────────────────────
  submitScore: async (itemId, criteriaId, score, note) => {
    const { isOnline, currentSession } = get();
    if (!currentSession) return;

    const sessionId = currentSession.id;
    const now = new Date();

    // Optimistic update — write to myScores immediately so the UI reflects the change
    const optimisticScore: GroupScore = {
      id: generateUUID(),
      workspaceId: currentSession.workspaceId,
      sessionId,
      itemId,
      criteriaId,
      userId: '', // filled below after auth lookup; fine for optimistic display
      score,
      note,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({
      myScores: {
        ...state.myScores,
        [itemId]: {
          ...(state.myScores[itemId] ?? {}),
          [criteriaId]: optimisticScore,
        },
      },
    }));

    if (!isOnline) {
      // Queue for later submission
      const pending: PendingScore = {
        tempId: generateUUID(),
        itemId,
        criteriaId,
        score,
        note,
        queuedAt: now,
      };
      set((state) => ({ pendingScores: [...state.pendingScores, pending] }));
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      // Treat as offline
      const pending: PendingScore = {
        tempId: generateUUID(),
        itemId,
        criteriaId,
        score,
        note,
        queuedAt: now,
      };
      set((state) => ({
        isOnline: false,
        pendingScores: [...state.pendingScores, pending],
      }));
      return;
    }

    try {
      const { error } = await supabase.rpc('submit_group_score', {
        p_session_id: sessionId,
        p_item_id: itemId,
        p_criteria_id: criteriaId,
        p_score: score,
        p_note: sanitizeTextOrNull(note),
      });
      if (error) {
        console.error('[GroupSessionStore] submit_group_score RPC error:', error.message);
        set({ error: error.message });
      }
    } catch (err) {
      console.error('[GroupSessionStore] submitScore error:', err);
    }

    // Update presence to reflect scoring progress
    const { presenceMembers } = get();
    const scoredCount = Object.values(get().myScores).reduce(
      (total, critMap) => total + Object.keys(critMap).length,
      0,
    );
    const optionCount = get().options.length;
    const criteriaCount = get().criteria.length;
    const totalCells = optionCount * criteriaCount;
    const myPresence = presenceMembers.find((m) => m.userId === ''); // placeholder lookup
    if (myPresence) {
      get().broadcastPresence(sessionId, {
        ...myPresence,
        optionsScored: scoredCount,
        totalOptions: totalCells,
        status: scoredCount >= totalCells ? 'complete' : 'scoring',
      });
    }
  },

  // ── flushPendingScores ────────────────────────────────────────────────────────
  flushPendingScores: async () => {
    const { pendingScores, currentSession } = get();
    if (!currentSession || pendingScores.length === 0) return;

    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const sessionId = currentSession.id;
    const succeeded: string[] = [];

    for (const pending of pendingScores) {
      try {
        const { error } = await supabase.rpc('submit_group_score', {
          p_session_id: sessionId,
          p_item_id: pending.itemId,
          p_criteria_id: pending.criteriaId,
          p_score: pending.score,
          p_note: sanitizeTextOrNull(pending.note),
        });
        if (!error) {
          succeeded.push(pending.tempId);
        } else {
          console.warn('[GroupSessionStore] flushPendingScores: RPC error:', error.message);
        }
      } catch (err) {
        console.warn('[GroupSessionStore] flushPendingScores: exception:', err);
      }
    }

    // Remove successfully flushed entries
    if (succeeded.length > 0) {
      set((state) => ({
        pendingScores: state.pendingScores.filter((p) => !succeeded.includes(p.tempId)),
      }));
    }
  },

  // ── openGroupSession ──────────────────────────────────────────────────────────
  openGroupSession: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return;
    }
    try {
      // Optimistic status update
      set((state) => ({
        currentSession: state.currentSession
          ? { ...state.currentSession, groupSessionStatus: 'OPEN' }
          : state.currentSession,
      }));

      const { error } = await supabase.rpc('open_group_session', {
        p_session_id: sessionId,
      });
      if (error) {
        console.error('[GroupSessionStore] open_group_session RPC error:', error.message);
        // Revert optimistic update
        set((state) => ({
          currentSession: state.currentSession
            ? { ...state.currentSession, groupSessionStatus: 'SETUP' }
            : state.currentSession,
          error: error.message,
        }));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] openGroupSession error:', err);
      set({ error: message });
    }
  },

  // ── startScoring ──────────────────────────────────────────────────────────────
  // §2.3a compliance: RPC is called first (authority). Local state is updated
  // only after the RPC confirms success — not before (no optimistic flip).
  // The Realtime postgres_changes event will also arrive and set the same
  // value, which is idempotent.
  startScoring: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return;
    }
    try {
      set({ error: null });
      const { error } = await supabase.rpc('start_group_scoring', {
        p_session_id: sessionId,
      });
      if (error) {
        console.error('[GroupSessionStore] start_group_scoring RPC error:', error.message);
        set({ error: error.message });
        return;
      }
      // RPC succeeded — update local cache to reflect confirmed DB state.
      // Realtime will also push this change; the set here prevents a blank
      // screen while waiting for the channel event.
      set((state) => ({
        currentSession: state.currentSession
          ? { ...state.currentSession, groupSessionStatus: 'SCORING' }
          : state.currentSession,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] startScoring error:', err);
      set({ error: message });
    }
  },

  // ── revealResults ─────────────────────────────────────────────────────────────
  revealResults: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      const msg = 'Could not reveal results: database not available.';
      set({ revealError: msg });
      return { ok: false, error: msg };
    }
    try {
      set({ revealAnimating: true, revealError: null });

      const { error } = await supabase.rpc('reveal_group_results', {
        p_session_id: sessionId,
      });
      if (error) {
        console.error('[GroupSessionStore] reveal_group_results RPC error:', error.message);
        const msg = `Could not reveal results: ${error.message}`;
        set({ revealAnimating: false, revealError: msg });
        return { ok: false, error: msg };
      }

      // Update status; the realtime channel will push the event, but update optimistically
      set((state) => ({
        currentSession: state.currentSession
          ? { ...state.currentSession, groupSessionStatus: 'REVEALING' }
          : state.currentSession,
      }));

      // Load results now that the transition has succeeded
      await get().loadResults(sessionId);
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] revealResults error:', err);
      const msg = `Could not reveal results: ${message}`;
      set({ revealAnimating: false, revealError: msg });
      return { ok: false, error: msg };
    }
  },

  // ── selectWinner ──────────────────────────────────────────────────────────────
  selectWinner: async (sessionId, itemId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      const msg = 'Could not select the winner: database not available.';
      set({ selectWinnerError: msg });
      return { ok: false, error: msg };
    }
    try {
      set({ selectWinnerError: null });
      const { error } = await supabase.rpc('select_group_winner', {
        p_session_id: sessionId,
        p_item_id: itemId,
      });
      if (error) {
        console.error('[GroupSessionStore] select_group_winner RPC error:', error.message);
        const msg = `Could not select the winner: ${error.message}`;
        set({ selectWinnerError: msg });
        return { ok: false, error: msg };
      }

      set((state) => ({
        currentSession: state.currentSession
          ? { ...state.currentSession, groupSessionStatus: 'DECIDED' }
          : state.currentSession,
      }));

      // Persist Post-S6 Results (Group Recommendation) — immutable once written.
      const { results, currentSession } = get();
      if (results) {
        const snapshot = {
          ...results,
          artifactKind: 'group_recommendation' as const,
          preS6Snapshot: currentSession?.preS6Snapshot,
        };
        const { error: snapshotError } = await supabase.rpc('record_post_s6_snapshot', {
          p_session_id: sessionId,
          p_snapshot: snapshot,
        });
        if (snapshotError) {
          // The winner IS recorded; only the results snapshot is missing.
          // Say so rather than showing plain success.
          console.warn('[GroupSessionStore] record_post_s6_snapshot RPC error:', snapshotError.message);
          const msg = `Winner selected, but the results snapshot was not saved: ${snapshotError.message}`;
          set({ selectWinnerError: msg });
          return { ok: true, error: msg };
        }
      }
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[GroupSessionStore] selectWinner error:', err);
      const msg = `Could not select the winner: ${message}`;
      set({ selectWinnerError: msg });
      return { ok: false, error: msg };
    }
  },

  // ── capturePreS6Snapshot ──────────────────────────────────────────────────────
  capturePreS6Snapshot: async (sessionId, snapshot) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    try {
      const { error } = await supabase.rpc('capture_pre_s6_snapshot', {
        p_session_id: sessionId,
        p_snapshot: snapshot,
      });
      if (error) {
        console.warn('[GroupSessionStore] capture_pre_s6_snapshot RPC error:', error.message);
        return;
      }
      // Update local store so PreS6ResultsPanel renders immediately
      set((state) => ({
        currentSession: state.currentSession
          ? { ...state.currentSession, preS6Snapshot: snapshot }
          : state.currentSession,
      }));
    } catch (err) {
      console.warn('[GroupSessionStore] capturePreS6Snapshot error:', err);
    }
  },

  // ── loadResults ───────────────────────────────────────────────────────────────
  loadResults: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Database not available' });
      return;
    }
    try {
      const { data, error } = await supabase.rpc('get_group_results', {
        p_session_id: sessionId,
      });
      if (error) {
        console.warn('[GroupSessionStore] get_group_results RPC error:', error.message);
        // Fall back to client-side computation from allScores if available
        const { criteria, options, allScores, currentSession } = get();
        if (allScores.length > 0 && criteria.length > 0 && options.length > 0 && currentSession) {
          const presenceMembers = get().presenceMembers;
          const computed = computeGroupResults(
            criteria,
            options,
            allScores,
            currentSession.divergenceThreshold,
            presenceMembers.map((m) => ({ userId: m.userId, displayName: m.displayName })),
          );
          set({ results: computed, revealAnimating: false });
        }
        return;
      }
      set({ results: data as GroupSessionResults, revealAnimating: false });
    } catch (err) {
      console.error('[GroupSessionStore] loadResults error:', err);
      set({ revealAnimating: false });
    }
  },

  // ── subscribeToGroupSession ───────────────────────────────────────────────────
  subscribeToGroupSession: async (sessionId, workspaceId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    // Guard against double-subscription
    if (get()._sessionChannel) return;

    // Helper: full re-load on reconnect to reconcile any missed events
    const reconcile = async () => {
      await get().flushPendingScores();
      await get().loadGroupSession(sessionId);
    };

    // ── Session row changes (status transitions) ──────────────────────────────
    const sessionChannel = supabase
      .channel(`group-session:${sessionId}:data`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'prioritization_sessions',
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const updatedStatus = row['group_session_status'] as GroupSessionStatus | undefined;
          if (updatedStatus) {
            set((state) => ({
              currentSession: state.currentSession
                ? { ...state.currentSession, groupSessionStatus: updatedStatus }
                : state.currentSession,
            }));
          }
        },
      )
      // ── session_events INSERT ─────────────────────────────────────────────────
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'session_events',
          filter: `session_id=eq.${sessionId}`,
        },
        async (payload) => {
          const event = payload.new as {
            event_type: string;
            payload: Record<string, unknown>;
          };

          switch (event.event_type) {
            case 'scoring_opened': {
              set((state) => ({
                currentSession: state.currentSession
                  ? { ...state.currentSession, groupSessionStatus: 'OPEN' }
                  : state.currentSession,
              }));
              break;
            }

            case 'results_revealing': {
              set({ revealAnimating: true });
              // Populate allScores and results from the event payload if present
              const payloadScores = event.payload['all_scores'] as
                | Record<string, unknown>[]
                | undefined;
              const payloadResults = event.payload['results'] as GroupSessionResults | undefined;

              if (payloadScores) {
                const allScores = payloadScores.map((r) => rowToScore(r));
                set({ allScores });
              }
              if (payloadResults) {
                set({
                  results: payloadResults,
                  revealAnimating: false,
                  currentSession: get().currentSession
                    ? { ...get().currentSession!, groupSessionStatus: 'REVEALING' }
                    : null,
                });
              } else {
                // Compute client-side and fetch from RPC
                await get().loadResults(sessionId);
                set((state) => ({
                  currentSession: state.currentSession
                    ? { ...state.currentSession, groupSessionStatus: 'REVEALING' }
                    : state.currentSession,
                }));
              }
              break;
            }

            case 'winner_decided': {
              set((state) => ({
                currentSession: state.currentSession
                  ? { ...state.currentSession, groupSessionStatus: 'DECIDED' }
                  : state.currentSession,
                revealAnimating: false,
              }));
              break;
            }

            case 'member_joined':
            case 'member_progress_updated': {
              // Presence channel is the authoritative source for member state;
              // these events are supplementary for members not on the presence channel yet.
              break;
            }

            default:
              break;
          }
        },
      )
      // ── Reconnect handler ─────────────────────────────────────────────────────
      .on('system', {}, async (payload) => {
        const status = (payload as { status?: string }).status;
        if (status === 'SUBSCRIBED') {
          set({ isOnline: true });
          await reconcile();
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          set({ isOnline: true });
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          set({ isOnline: false });
        } else if (status === 'CLOSED') {
          set({ isOnline: false });
        }
      });

    set({ _sessionChannel: sessionChannel });

    // ── Presence channel ───────────────────────────────────────────────────────
    const presenceChannel = supabase.channel(
      `group-session:${sessionId}:presence`,
      {
        config: { presence: { key: sessionId } },
      },
    );

    const syncPresence = () => {
      const state = presenceChannel.presenceState<GroupMemberPresence>();
      const members: GroupMemberPresence[] = [];
      for (const presences of Object.values(state)) {
        for (const meta of (presences as unknown as { metas: GroupMemberPresence[] }).metas ?? presences as unknown as GroupMemberPresence[]) {
          members.push(meta as unknown as GroupMemberPresence);
        }
      }
      set({ presenceMembers: members });
    };

    presenceChannel
      .on('presence', { event: 'sync' }, syncPresence)
      .on('presence', { event: 'join' }, syncPresence)
      .on('presence', { event: 'leave' }, syncPresence)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Write a session_events row to announce this member
          try {
            const {
              data: { user },
            } = await getSupabaseClient().auth.getUser();
            if (user) {
              await supabase.from('session_events').insert({
                session_id: sessionId,
                workspace_id: workspaceId,
                actor_id: user.id,
                actor_name: user.email ?? user.id,
                event_type: 'member_joined',
                payload: { user_id: user.id },
              });
            }
          } catch {
            // Non-critical; ignore
          }
        }
      });

    set({ _presenceChannel: presenceChannel });
  },

  // ── unsubscribeFromGroupSession ───────────────────────────────────────────────
  unsubscribeFromGroupSession: (_sessionId) => {
    const supabase = getSafeSupabaseClient();
    const { _sessionChannel, _presenceChannel } = get();

    if (_presenceChannel) {
      try {
        void _presenceChannel.untrack();
        if (supabase) void supabase.removeChannel(_presenceChannel);
      } catch {
        // Ignore cleanup errors
      }
    }
    if (_sessionChannel && supabase) {
      void supabase.removeChannel(_sessionChannel);
    }

    set({ _sessionChannel: null, _presenceChannel: null, isOnline: false });
  },

  // ── broadcastPresence ─────────────────────────────────────────────────────────
  broadcastPresence: (sessionId, presence) => {
    void sessionId; // channel is already scoped to the session
    const { _presenceChannel } = get();
    if (!_presenceChannel) return;

    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    // Resolve userId and displayName from auth session without blocking the caller
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      const displayName: string =
        (user.user_metadata?.full_name as string | undefined)?.trim() ||
        (user.user_metadata?.name as string | undefined)?.trim() ||
        user.email?.split('@')[0] ||
        'Participant';
      const full: GroupMemberPresence = { ...presence, userId: user.id, displayName };
      void _presenceChannel.track(full);
    }).catch(() => {
      // Non-critical; ignore
    });
  },

  // ── resetGroupSession ─────────────────────────────────────────────────────────
  resetGroupSession: () => {
    const supabase = getSafeSupabaseClient();
    const { _sessionChannel, _presenceChannel } = get();

    if (_presenceChannel) {
      try {
        void _presenceChannel.untrack();
        if (supabase) void supabase.removeChannel(_presenceChannel);
      } catch {
        // Ignore
      }
    }
    if (_sessionChannel && supabase) {
      void supabase.removeChannel(_sessionChannel);
    }

    set({
      currentSession: null,
      criteria: [],
      options: [],
      myScores: {},
      allScores: [],
      results: null,
      presenceMembers: [],
      pendingScores: [],
      isOnline: true,
      isLoading: false,
      error: null,
      revealAnimating: false,
      _presenceChannel: null,
      _sessionChannel: null,
    });
  },
});

export const useGroupSessionStore = create<GroupSessionStoreState>()(storeCreator);
